import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { css } from '@bd/bdui-styles/emotion';
import { SmoothLink } from '@bd/bdcore-utilities';
import ImageWrapper from '../../components/ImageWrapper';
import AgentBio from './AgentBio';
import Listings from './Listings';
import { colors, typography } from '../../styles';

const styles = css`
  padding: 54px 14px 84px;
  @media (min-width: 360px) {
    padding-left: 27px;
    padding-right: 27px;
  }
  @media (min-width: 768px) {
    padding: 72px 60px 98px;
  }
  @media (min-width: 992px) {
    padding: 110px 90px 160px;
  }
  @media (min-width: 1280px) {
    padding-left: 135px;
    padding-right: 135px;
  }
  .AgentContent_inner {
    @media (min-width: 1280px) {
      display: flex;
      max-width: 1440px;
      margin: 0 auto;
    }
  }
  .AgentContent_header {
    font-family: ${typography.SansSerif};
    font-size: 36px;
    font-weight: normal;
    line-height: 1.1;
    text-align: center;
    color: ${colors.color4};
    @media (max-width: 767.98px) {
      position: relative;
      padding-bottom: 24px;
      margin-bottom: 27px;
      &::after {
        content: '';
        display: block;
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 75px;
        height: 3px;
        background-color ${colors.color2};
      }
    }
    @media (min-width: 768px) {
      font-size: 43px;
      text-align: left;
      margin-bottom: 0.333333em;
    }
    @media (min-width: 992px) {
      font-size: 48px;
    }
  }
  .AgentContent_agentInfo {
    margin-top: 28px;
    @media (min-width: 768px) {
      margin-top: 0;
      width: 100%;
    }
  }
  .AgentContent_photo {
    @media (min-width: 768px) {
      width: 264px;
      height: 352px;
      margin-right: 36px;
      margin-bottom: 30px;
      float: left;
    }
    @media (min-width: 992px) {
      width: 306px;
      height: 408px;
      margin-right: 64px;
      margin-bottom: 52px;
    }
    @media (min-width: 1280px) {
      width: 330px;
      height: 440px;
      margin-bottom: 0;
      flex-shrink: 0;
    }
  }
  .AgentContent_photo .ImageWrapper_image {
    @media (max-width: 767.98px) {
      position: static;
      width: 100%;
      height: auto;
    }
  }
  .AgentContent_contactInfo {
    text-align: center;
    @media (min-width: 768px) {
      text-align: left;
    }
    @media (min-width: 1280px) {
      display: flex;
      flex-wrap: wrap;
      margin-bottom: 60px;
    }
  }
  .AgentContent_phones {
    @media (min-width: 1280px) {
      display: inline-block;
      margin-right: 80px;
    }
  }
  .AgentContent_adtlContacts {
    @media (min-width: 1280px) {
      display: inline-block;
    }
  }
  .AgentContent_listingLinkWrapper {
    margin-top: 28px;
    @media (min-width: 768px) {
      margin-top: 14px;
    }
    @media (min-width: 992px) {
      margin-top: 20px;
    }
    @media (min-width: 1280px) {
      width: 100%;
      margin-top: 28px;
    }
    @media (min-width: 1440px) {
      width: auto;
      display: flex;
      align-items: center;
      margin-top: 0;
      margin-left: auto;
    }
  }
  .AgentContent_listingLink {
    display: inline-block;
    background-color: ${colors.color2};
    color: white;
    font-size: 13px;
    font-weight: bold;
    line-height: 1.25;
    color: white;
    padding: 8px 26px;
    &:focus {
      outline: 1px dashed ${colors.color3};
      outline-offset: 2px;
      background-color: ${colors.color1Light};
    }
    @media (min-width: 768px) {
      font-size: 14px;
      padding: 9px 30px;
    }
    @media (min-width: 992px) {
      font-size: 15px;
      padding: 10px 34px;
      transition: background-color 250ms ease;
      &:hover {
        background-color: ${colors.color1Light};
      }
    }
  }
  /* .AgentContent_linkWrapper is only necessary so that the focus outline around
     .AgentContent_link doens't sit on top of the picture when the picture is floated left */
  .AgentContent_linkWrapper {
    @media (max-width: 767.98px) {
      &:last-child {
        .AgentContent_link:last-child {
          margin-bottom: 0;
        }
      }
    }
    @media (min-width: 1280px) {
      &:first-child:not(:only-child) {
        margin-bottom: 6px;
      }
    }
  }
  .AgentContent_link {
    display: inline-block;
    font-size: 15px;
    margin-bottom: 0.777778em;
    font-weight: bold;
    color: ${colors.color1Light};
    &:focus {
      outline: 1px dashed ${colors.color3};
      text-decoration: underline;
    }
    @media (min-width: 768px) {
      font-size: 16px;
    }
    @media (min-width: 992px) {
      font-size: 18px;
      &:hover {
        color: ${colors.color1};
        text-decoration: underline;
      }
    }
    @media (min-width: 1280px) {
      display: block;
      margin-bottom: 0;
    }
  }
  .AgentContent_icon {
    display: inline-block;
    margin-right: 12px;
  }
  .AgentContent_icon-phone {
    width: 15px;
    @media (min-width: 768px) {
      width: 17px;
    }
    @media (min-width: 992px) {
      width: 20px;
    }
  }
  .AgentContent_icon-email,
  .AgentContent_icon-web {
    width: 16px;
    @media (min-width: 768px) {
      width: 18px;
    }
    @media (min-width: 992px) {
      width: 21px;
    }
  }
  .AgentContent_icon-web {
    margin-bottom: 1px;
    @media (min-width: 768px) {
      margin-bottom: 0;
    }
  }
  .AgentContent_newWindowIcon {
    width: 8px;
    height: 8px;
    vertical-align: super;
    margin-left: 2px;
  }
`;


function AgentContent({ agent }) {
  const fullName = get(agent, 'name', '');
  const displayName = agent.custom_name || fullName.split(' ')[0];
  const photoUrl = get(agent, 'logo.contentUrl');
  const primaryPhone = get(agent, 'telephone');
  const secondaryPhone = get(agent, 'secondary_phone');
  const email = get(agent, 'email');
  const website = get(agent, 'website');
  const bio = get(agent, 'formatted_bio', '');
  const listings = get(agent, 'listings', []);
  const hasListings = listings.length > 0;
  const phoneRegex = /\D*(\d{3})\D*(\d{3})\D*(\d{4})/;

  return (
    <section className={styles}>
      <div className="AgentContent_inner">
        {
          photoUrl &&
          <ImageWrapper
            className="AgentContent_photo"
            image={{
              src: photoUrl,
              alt: fullName,
              width: 600,
              height: 600,
            }}
          />
        }
        <div className="AgentContent_agentInfo">
          <h2 className="AgentContent_header">{agent.name}</h2>
          <div className="AgentContent_contactInfo">
            {
              (primaryPhone || secondaryPhone) &&
              <div className="AgentContent_phones">
                {
                  primaryPhone &&
                  <div className="AgentContent_linkWrapper">
                    <a
                      className="AgentContent_link"
                      href={`tel:${primaryPhone.replace(/[()-\s]/g, '')}`}
                      title="main phone number"
                    >
                      <img
                        className="AgentContent_icon AgentContent_icon-phone"
                        src="/images/icon-telephone-dark.svg"
                        alt="telephone"
                      />
                      {primaryPhone.replace(phoneRegex, '($1) $2-$3')}
                    </a>
                  </div>
                }
                {
                  secondaryPhone &&
                  <div className="AgentContent_linkWrapper">
                    <a
                      className="AgentContent_link"
                      href={`tel:${secondaryPhone.replace(/[()-\s]/g, '')}`}
                      title="alternate phone number"
                    >
                      <img
                        className="AgentContent_icon AgentContent_icon-phone"
                        src="/images/icon-telephone-dark.svg"
                        alt="telephone"
                      />
                      {secondaryPhone.replace(phoneRegex, '($1) $2-$3')}
                    </a>
                  </div>
                }
              </div>
            }
            {
              (email || website) &&
              <div className="AgentContent_adtlContacts">
                {
                  email &&
                  <div className="AgentContent_linkWrapper">
                    <a className="AgentContent_link" href={`mailto:${email}`}>
                      <img
                        className="AgentContent_icon AgentContent_icon-email"
                        src="/images/icon-envelope-line-dark.svg"
                        alt=""
                      />
                      {`Email ${displayName}`}
                    </a>
                  </div>
                }
                {
                  website &&
                  <div className="AgentContent_linkWrapper">
                    <a className="AgentContent_link" href={website} target="_blank" rel="noopener noreferrer">
                      <img
                        className="AgentContent_icon AgentContent_icon-web"
                        src="/images/icon-world-wide-web.svg"
                        alt=""
                      />
                      <span className="text-nowrap">
                        {`Visit ${displayName}’s Website`}
                        <img
                          className="AgentContent_newWindowIcon"
                          src="/images/icon-new-window.svg"
                          alt="opens in a new tab"
                        />
                      </span>
                    </a>
                  </div>
                }
              </div>
            }
            {
              hasListings &&
              <div className="AgentContent_listingLinkWrapper">
                <SmoothLink className="AgentContent_listingLink" href="#listings">
                  {`View ${displayName}’s Listings`}
                </SmoothLink>
              </div>
            }
          </div>
          {
            bio.length > 0 &&
            <AgentBio bio={bio} />
          }
        </div>
      </div>
      {
        hasListings &&
        <Listings listings={listings} agentName={displayName} />
      }
    </section>
  );
}

AgentContent.propTypes = {
  agent: PropTypes.object.isRequired,
};

export default AgentContent;



// WEBPACK FOOTER //
// ./src/pages/AgentDetail/AgentContent.js